/**
 * The "Rounded" nav icon set — original chunky filled shapes, drawn for this app rather than
 * pulled from a pack (owner brief 2026-08-01: the emoji tabs were "super dumb"; this is the
 * friendly-but-not-emoji end of the answer, the lucide "Line" set is the grown-up end).
 *
 * House rules for anything added here:
 *  - 24×24 viewBox, everything `fill="currentColor"` — never a hard-coded color, never a
 *    stroke. The nav tints these with `text-accent`/`text-fg-3` and every theme, including
 *    the wild ones, works for free.
 *  - Interior detail is knocked *out* with `fill-rule="evenodd"` inside a single path, so the
 *    page background shows through. That's what keeps them legible at 20px on any surface —
 *    a second fill color would have to guess which surface it's sitting on.
 *  - Shapes that only need to *union* (a calendar's hangers, a camera's bump) are separate
 *    sibling elements; siblings merge for free, whereas unioning inside one evenodd path
 *    would punch a hole wherever two subpaths overlap.
 *  - Corner radii stay generous (≥1.5 at this scale) and detail stays coarse: anything
 *    thinner than ~1.2 units disappears at a 20px render.
 */

export interface NavIconGlyphProps {
  className?: string;
  /** Accepted and ignored — lets a drawn glyph stand in for a lucide icon in the same map.
   * Typed as widely as lucide types it so the two are assignable to one `Glyph`. */
  strokeWidth?: string | number;
}

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  'aria-hidden': true,
  focusable: 'false',
} as const;

/** Ray/tooth positions for the radially symmetric glyphs. */
const SUN_RAYS = [0, 45, 90, 135, 180, 225, 270, 315];
const GEAR_TEETH = [0, 60, 120, 180, 240, 300];

/** Circle as a path subpath — used for knocked-out holes inside an evenodd path. */
function hole(cx: number, cy: number, r: number): string {
  return `M${cx + r} ${cy}A${r} ${r} 0 1 0 ${cx - r} ${cy}A${r} ${r} 0 1 0 ${cx + r} ${cy}Z`;
}

/** Horizontal capsule (a rounded bar whose radius is half its height) as a path subpath. */
function bar(x1: number, x2: number, y: number, h: number): string {
  const r = h / 2;
  return `M${x1} ${y}H${x2}A${r} ${r} 0 0 1 ${x2} ${y + h}H${x1}A${r} ${r} 0 0 1 ${x1} ${y}Z`;
}

/** Calendar — a rounded pad with two hangers, a torn-off header rule and a grid of days. */
export function RoundedCalendar({ className }: NavIconGlyphProps): JSX.Element {
  return (
    <svg {...svgProps} className={className}>
      <rect x="6.6" y="1.6" width="3.2" height="5" rx="1.6" />
      <rect x="14.2" y="1.6" width="3.2" height="5" rx="1.6" />
      <path
        fillRule="evenodd"
        d={[
          'M7 4.2H17A4.5 4.5 0 0 1 21.5 8.7V17.5A4.5 4.5 0 0 1 17 22H7A4.5 4.5 0 0 1 2.5 17.5V8.7A4.5 4.5 0 0 1 7 4.2Z',
          'M2.5 9.5H21.5V11H2.5Z',
          hole(7.9, 14.4, 1.45),
          hole(12, 14.4, 1.45),
          hole(16.1, 14.4, 1.45),
          hole(7.9, 18.3, 1.45),
          hole(12, 18.3, 1.45),
        ].join('')}
      />
    </svg>
  );
}

/** Lists — a clipboard with its clip, three ticks and three lines of list. */
export function RoundedLists({ className }: NavIconGlyphProps): JSX.Element {
  return (
    <svg {...svgProps} className={className}>
      <rect x="8.4" y="1.4" width="7.2" height="4.4" rx="2.2" />
      <path
        fillRule="evenodd"
        d={[
          'M7.9 3.2H16.1A4.5 4.5 0 0 1 20.6 7.7V17.1A4.5 4.5 0 0 1 16.1 21.6H7.9A4.5 4.5 0 0 1 3.4 17.1V7.7A4.5 4.5 0 0 1 7.9 3.2Z',
          hole(8.3, 9.3, 1.3),
          hole(8.3, 13.2, 1.3),
          hole(8.3, 17.1, 1.3),
          bar(12.25, 16.65, 8.35, 1.9),
          bar(12.25, 16.65, 12.25, 1.9),
          bar(12.25, 16.65, 16.15, 1.9),
        ].join('')}
      />
    </svg>
  );
}

/** Check-in — a fat little camera: body, viewfinder bump, ring lens and a flash pip. */
export function RoundedCheckin({ className }: NavIconGlyphProps): JSX.Element {
  return (
    <svg {...svgProps} className={className}>
      <rect x="6.8" y="3" width="8" height="6" rx="2.6" />
      <path
        fillRule="evenodd"
        d={[
          'M6.4 5.8H17.6A4.6 4.6 0 0 1 22.2 10.4V16.6A4.6 4.6 0 0 1 17.6 21.2H6.4A4.6 4.6 0 0 1 1.8 16.6V10.4A4.6 4.6 0 0 1 6.4 5.8Z',
          hole(12, 13.5, 4.6),
          hole(18.4, 9.6, 1.15),
        ].join('')}
      />
      <circle cx="12" cy="13.5" r="2.6" />
    </svg>
  );
}

/** Recipes — a closed cookbook: spine strip down the left, a ribbon bookmark hanging out of
 * the top, and two title rules. The ribbon is what makes it read as a *book* rather than a
 * notepad at 20px; a title plate alone (the first pass) did not. */
export function RoundedRecipes({ className }: NavIconGlyphProps): JSX.Element {
  return (
    <svg {...svgProps} className={className}>
      <path
        fillRule="evenodd"
        d={[
          'M7.1 2.2H17.1A3.2 3.2 0 0 1 20.3 5.4V18.6A3.2 3.2 0 0 1 17.1 21.8H7.1A3.2 3.2 0 0 1 3.9 18.6V5.4A3.2 3.2 0 0 1 7.1 2.2Z',
          'M7.4 2.2H8.7V21.8H7.4Z',
          'M14.3 2.2H17.5V11.4L15.9 9.6L14.3 11.4Z',
          bar(10.6, 18.2, 14.2, 1.7),
          bar(10.6, 15.9, 17.4, 1.7),
        ].join('')}
      />
    </svg>
  );
}

/** Digest — the day's sun. */
export function RoundedDigest({ className }: NavIconGlyphProps): JSX.Element {
  return (
    <svg {...svgProps} className={className}>
      <circle cx="12" cy="12" r="5.1" />
      {SUN_RAYS.map((angle) => (
        <rect
          key={angle}
          x="10.8"
          y="0.8"
          width="2.4"
          height="4"
          rx="1.2"
          transform={`rotate(${angle} 12 12)`}
        />
      ))}
    </svg>
  );
}

/** Settings — a six-tooth cog. Teeth are siblings (union); only the bore is knocked out.
 * The first pass used a big r=8.6 body with short teeth and read as a flower, not a gear —
 * the body came down to 7.4 and the teeth grew so they clearly *protrude*. */
export function RoundedSettings({ className }: NavIconGlyphProps): JSX.Element {
  return (
    <svg {...svgProps} className={className}>
      {GEAR_TEETH.map((angle) => (
        <rect
          key={angle}
          x="9.4"
          y="0.7"
          width="5.2"
          height="6.4"
          rx="1.9"
          transform={`rotate(${angle} 12 12)`}
        />
      ))}
      <path fillRule="evenodd" d={`${hole(12, 12, 7.4)}${hole(12, 12, 3.1)}`} />
    </svg>
  );
}
